import { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, errMsg } from "@/lib/api";
import { fmt } from "@/lib/dates";

export const AiScheduleDialog = ({ weekStart, onSaved }) => {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [draft, setDraft] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const reset = (o) => {
    setOpen(o);
    if (!o) setDraft(null);
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/schedule-draft", { week_start: weekStart, instructions });
      setDraft(data);
      setPicked(new Set(data.shifts.map((_, i) => i)));
      if (!data.shifts.length) toast.info("De AI vond geen nieuwe diensten om voor te stellen.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    const shifts = draft.shifts.filter((_, i) => picked.has(i)).map(({ name, ...s }) => ({ ...s, note: "AI-voorstel" }));
    try {
      const { data } = await api.post("/shifts/bulk", { shifts });
      toast.success(`${data.created} ${data.created === 1 ? "dienst" : "diensten"} ingepland`);
      onSaved();
      reset(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (i) => setPicked((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });

  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="ai-schedule-button">
        <Sparkles className="h-4 w-4 mr-2 text-[#FF5A36]" />Rooster met AI
      </Button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Roostervoorstel met AI</DialogTitle>
            <DialogDescription>De AI kijkt naar je team, beschikbaarheid, contracturen en vorige week. Jij kiest wat je opslaat.</DialogDescription>
          </DialogHeader>
          {!draft ? (
            <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={4} placeholder="Bijv. open di t/m zo 16:00-22:00, vrijdag en zaterdag 2 man extra in de keuken, Noor kan niet op maandag" data-testid="ai-schedule-instructions-input" />
          ) : (
            <div className="space-y-3" data-testid="ai-schedule-draft">
              {draft.notes && <p className="text-sm text-slate-600 rounded-xl bg-[#FAFAF7] p-3" data-testid="ai-schedule-notes">{draft.notes}</p>}
              {draft.skipped_unavailable > 0 && <p className="text-xs text-amber-700" data-testid="ai-schedule-skipped">{draft.skipped_unavailable} voorstel(len) weggelaten omdat de medewerker dan niet beschikbaar is.</p>}
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
                {draft.shifts.map((s, i) => (
                  <li key={i} className="flex items-center gap-3 p-2.5 text-sm">
                    <Checkbox checked={picked.has(i)} onCheckedChange={() => toggle(i)} data-testid={`ai-draft-shift-${i}`} />
                    <span className="w-20 capitalize text-slate-600">{fmt(s.date, "EEE d")}</span>
                    <span className="flex-1 font-medium truncate">{s.name}<span className="block text-xs text-slate-500">{s.position}</span></span>
                    <span className="font-mono-num font-semibold">{s.start}–{s.end}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter className="gap-2">
            {draft && <Button variant="ghost" onClick={() => setDraft(null)} data-testid="ai-schedule-back-button">Opnieuw</Button>}
            {!draft ? (
              <Button onClick={generate} disabled={busy} data-testid="ai-schedule-generate-button">{busy ? "Rooster wordt gemaakt…" : "Maak voorstel"}</Button>
            ) : (
              <Button onClick={save} disabled={busy || picked.size === 0} data-testid="ai-schedule-save-button">{picked.size} {picked.size === 1 ? "dienst" : "diensten"} opslaan</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
